import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Activity,
    AlertTriangle,
    Bot,
    CheckCircle2,
    Download,
    ExternalLink,
    FileCheck2,
    FileText,
    LayoutGrid,
    Loader2,
    MessageSquare,
    Package,
    RefreshCw,
    Scale,
    Send,
    XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { crud, muvekkilMerkez, absoluteUrl, triggerDownload } from "@/lib/api";
import { AiMarkdown } from "@/components/ai/AiMarkdown";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { TABS } from "./muvekkilMerkez/constants";
import { KpiGrid, OzetPanel, RehberPanel } from "./muvekkilMerkez/panels";
import MesajPanel from "./muvekkilMerkez/MesajPanel";
import TahkimAkilPanel from "./muvekkilMerkez/TahkimAkilPanel";
import {
    ActivityTimeline,
    Badge,
    KpiCard,
    MerkezHero,
    PanelCard,
    SectionIntro,
    TabSidebar,
} from "./muvekkilMerkez/ui";

const ONAY_RENK = {
    BEKLIYOR: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    ONAYLI: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    REDDEDILDI: "text-rose-300 bg-rose-500/10 border-rose-500/30",
};

export default function MuvekkilMerkezPage() {
    const [tab, setTab] = useState("ozet");
    const [loading, setLoading] = useState(true);
    const [busyKey, setBusyKey] = useState(null);
    const [ozet, setOzet] = useState(null);
    const [evraklar, setEvraklar] = useState([]);
    const [onayFiltre, setOnayFiltre] = useState("BEKLIYOR");
    const [mesajlar, setMesajlar] = useState([]);
    const [aktiviteler, setAktiviteler] = useState([]);
    const [eksikDosyalar, setEksikDosyalar] = useState([]);
    const [yanitDosyaId, setYanitDosyaId] = useState("");
    const [yanitMetin, setYanitMetin] = useState("");
    const [redModal, setRedModal] = useState(null);
    const [redNeden, setRedNeden] = useState("");
    const [portalAkil, setPortalAkil] = useState(null);
    const [portalAkilLoading, setPortalAkilLoading] = useState(false);
    const [tahkimDosyalar, setTahkimDosyalar] = useState([]);
    const [tahkimDosyaId, setTahkimDosyaId] = useState("");
    const [tahkimChecklist, setTahkimChecklist] = useState(null);
    const [tahkimBrifing, setTahkimBrifing] = useState("");
    const [tahkimRedMetin, setTahkimRedMetin] = useState("");
    const [tahkimRedSonuc, setTahkimRedSonuc] = useState("");
    const [tahkimPaketler, setTahkimPaketler] = useState([]);
    const [tahkimKarsiPlaka, setTahkimKarsiPlaka] = useState("");
    const [tahkimSigortaBasvuruTarihi, setTahkimSigortaBasvuruTarihi] = useState("");
    const [tahkimAciklamalar, setTahkimAciklamalar] = useState("");
    const [tahkimTalepSonucu, setTahkimTalepSonucu] = useState("");
    const [tahkimOnizleme, setTahkimOnizleme] = useState(null);
    const [tahkimLoading, setTahkimLoading] = useState(false);

    const loadOzet = useCallback(async () => {
        const data = await muvekkilMerkez.ozet();
        setOzet(data);
    }, []);

    const loadOnay = useCallback(async () => {
        const data = await muvekkilMerkez.onayKuyrugu({
            onay_durumu: onayFiltre,
            limit: 120,
        });
        setEvraklar(data.evraklar || []);
    }, [onayFiltre]);

    const loadMesaj = useCallback(async () => {
        const data = await muvekkilMerkez.mesajSohbetler({ mod: "bekleyen", limit: 50 });
        setMesajlar(data.sohbetler || []);
    }, []);

    const loadAktivite = useCallback(async () => {
        const data = await muvekkilMerkez.aktivite({ limit: 60 });
        setAktiviteler(data.aktiviteler || []);
    }, []);

    const loadEksik = useCallback(async () => {
        const data = await muvekkilMerkez.eksikEvrak({ limit: 80 });
        setEksikDosyalar(data.dosyalar || []);
    }, []);

    const loadPortalAkil = useCallback(async () => {
        setPortalAkilLoading(true);
        try {
            const data = await muvekkilMerkez.portalAkil({ gun: 30 });
            setPortalAkil(data);
        } catch {
            toast.error("Portal Akıl verisi yüklenemedi.");
        } finally {
            setPortalAkilLoading(false);
        }
    }, []);

    const loadTahkimDosyalar = useCallback(async () => {
        const data = await muvekkilMerkez.tahkimDosyalar({ limit: 50 });
        setTahkimDosyalar(data.items || []);
        if (!tahkimDosyaId && data.items?.length) {
            setTahkimDosyaId(String(data.items[0].dosya_id));
        }
    }, [tahkimDosyaId]);

    const loadTahkimOnizleme = useCallback(
        async (dosyaId, overrides = {}) => {
            if (!dosyaId) return;
            const params = {
                dosya_id: dosyaId,
                karsi_plaka: overrides.karsi_plaka ?? tahkimKarsiPlaka,
                sigorta_basvuru_tarihi:
                    overrides.sigorta_basvuru_tarihi ?? tahkimSigortaBasvuruTarihi,
                aciklamalar: overrides.aciklamalar ?? tahkimAciklamalar,
                talep_sonucu: overrides.talep_sonucu ?? tahkimTalepSonucu,
            };
            const data = await muvekkilMerkez.tahkimDilekceOnizleme(params);
            setTahkimOnizleme(data);
            if (data.sigorta_basvuru_kaynak?.karsi_plaka_kaynak && !tahkimKarsiPlaka) {
                setTahkimKarsiPlaka(data.sigorta_basvuru_kaynak.karsi_plaka_kaynak);
            }
            if (data.uyusmazlik?.sigorta_basvuru_tarihi && data.uyusmazlik.sigorta_basvuru_tarihi !== "—") {
                const p = data.uyusmazlik.sigorta_basvuru_tarihi.split(".");
                if (p.length === 3 && !tahkimSigortaBasvuruTarihi) {
                    setTahkimSigortaBasvuruTarihi(`${p[2]}-${p[1]}-${p[0]}`);
                }
            }
            if (data.aciklamalar) setTahkimAciklamalar(data.aciklamalar);
            if (data.talep_sonucu) setTahkimTalepSonucu(data.talep_sonucu);
            return data;
        },
        [
            tahkimKarsiPlaka,
            tahkimSigortaBasvuruTarihi,
            tahkimAciklamalar,
            tahkimTalepSonucu,
        ]
    );

    const loadTahkimChecklist = useCallback(async (dosyaId) => {
        if (!dosyaId) return;
        const data = await muvekkilMerkez.tahkimChecklist({ dosya_id: dosyaId });
        setTahkimChecklist(data);
        setTahkimPaketler(data.paketler || []);
        const kayit = data.akil_kayitlari || {};
        setTahkimBrifing(kayit.son_brifing?.metin || "");
        setTahkimRedSonuc(kayit.son_red?.metin || "");
        if (kayit.son_red?.kaynak_metin) {
            setTahkimRedMetin(kayit.son_red.kaynak_metin);
        }
        const sonPaket = data.paketler?.[0];
        if (sonPaket?.karsi_plaka) {
            setTahkimKarsiPlaka(sonPaket.karsi_plaka);
        }
    }, []);

    const refreshAll = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadOzet(),
                loadOnay(),
                loadMesaj(),
                loadAktivite(),
                loadEksik(),
            ]);
        } catch (e) {
            toast.error("Veriler yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [loadOzet, loadOnay, loadMesaj, loadAktivite, loadEksik]);

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    useEffect(() => {
        if (tab === "onay") loadOnay().catch(() => {});
    }, [tab, onayFiltre, loadOnay]);

    useEffect(() => {
        if (tab === "mesaj") loadMesaj().catch(() => {});
    }, [tab, loadMesaj]);

    useEffect(() => {
        if (tab === "akil") loadPortalAkil().catch(() => {});
    }, [tab, loadPortalAkil]);

    useEffect(() => {
        if (tab !== "tahkim") return;
        setTahkimLoading(true);
        loadTahkimDosyalar()
            .catch(() => toast.error("Tahkim dosya listesi yüklenemedi."))
            .finally(() => setTahkimLoading(false));
    }, [tab, loadTahkimDosyalar]);

    useEffect(() => {
        if (tab !== "tahkim" || !tahkimDosyaId) return;
        loadTahkimChecklist(tahkimDosyaId).catch(() => {});
        muvekkilMerkez
            .tahkimDilekceOnizleme({ dosya_id: tahkimDosyaId })
            .then((data) => {
                setTahkimOnizleme(data);
                if (data.sigorta_basvuru_kaynak?.karsi_plaka_kaynak) {
                    setTahkimKarsiPlaka(data.sigorta_basvuru_kaynak.karsi_plaka_kaynak);
                }
                if (data.uyusmazlik?.sigorta_basvuru_tarihi && data.uyusmazlik.sigorta_basvuru_tarihi !== "—") {
                    const p = data.uyusmazlik.sigorta_basvuru_tarihi.split(".");
                    if (p.length === 3) {
                        setTahkimSigortaBasvuruTarihi(`${p[2]}-${p[1]}-${p[0]}`);
                    }
                }
                setTahkimAciklamalar(data.aciklamalar || "");
                setTahkimTalepSonucu(data.talep_sonucu || "");
            })
            .catch(() => {});
    }, [tab, tahkimDosyaId, loadTahkimChecklist]);

    async function handleTahkimBrifing() {
        if (!tahkimDosyaId) return;
        setBusyKey("tahkim-brifing");
        try {
            const data = await muvekkilMerkez.tahkimBrifing({ dosya_id: tahkimDosyaId });
            setTahkimBrifing(data.metin || "");
            toast.success("Savunma brifingi hazır.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Brifing üretilemedi.");
        } finally {
            setBusyKey(null);
        }
    }

    async function handleTahkimRedAnaliz() {
        if (!tahkimDosyaId || tahkimRedMetin.trim().length < 20) {
            toast.error("Red metnini yapıştırın (en az 20 karakter).");
            return;
        }
        setBusyKey("tahkim-red");
        try {
            const data = await muvekkilMerkez.tahkimRedAnaliz({
                dosya_id: tahkimDosyaId,
                red_metni: tahkimRedMetin.trim(),
            });
            setTahkimRedSonuc(data.metin || data.cevap_taslagi || "");
            toast.success("Red analizi tamamlandı.");
        } catch (e) {
            toast.error(e?.response?.data?.error || e?.response?.data?.detail || "Analiz yapılamadı.");
        } finally {
            setBusyKey(null);
        }
    }

    async function handleTahkimPaket() {
        if (!tahkimDosyaId) return;
        setBusyKey("tahkim-paket");
        try {
            const data = await muvekkilMerkez.tahkimPaket({
                dosya_id: tahkimDosyaId,
                karsi_plaka: tahkimKarsiPlaka.trim(),
                sigorta_basvuru_tarihi: tahkimSigortaBasvuruTarihi.trim(),
                aciklamalar: tahkimAciklamalar.trim(),
                talep_sonucu: tahkimTalepSonucu.trim(),
            });
            setTahkimPaketler(data.paketler || []);
            if (data.checklist) setTahkimChecklist(data.checklist);
            toast.success("Tahkim başvuru paketi oluşturuldu.");
        } catch (e) {
            toast.error(e?.response?.data?.error || e?.response?.data?.detail || "Paket oluşturulamadı.");
        } finally {
            setBusyKey(null);
        }
    }

    async function handleTahkimOnizlemeYenile() {
        if (!tahkimDosyaId) return;
        setBusyKey("tahkim-onizleme");
        try {
            setTahkimAciklamalar("");
            setTahkimTalepSonucu("");
            await loadTahkimOnizleme(tahkimDosyaId, {
                aciklamalar: "",
                talep_sonucu: "",
            });
            toast.success("Dilekçe önizlemesi güncellendi.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Önizleme yüklenemedi.");
        } finally {
            setBusyKey(null);
        }
    }

    async function handleOnayla(id) {
        setBusyKey(`onay-${id}`);
        try {
            await muvekkilMerkez.evrakOnayla(id);
            toast.success("Evrak onaylandı.");
            await Promise.all([loadOzet(), loadOnay()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Onaylanamadı.");
        } finally {
            setBusyKey(null);
        }
    }

    async function handleReddet() {
        if (!redModal) return;
        setBusyKey(`red-${redModal}`);
        try {
            await muvekkilMerkez.evrakReddet(redModal, redNeden);
            toast.success("Evrak reddedildi.");
            setRedModal(null);
            setRedNeden("");
            await Promise.all([loadOzet(), loadOnay()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reddedilemedi.");
        } finally {
            setBusyKey(null);
        }
    }

    async function handleYanit(e) {
        e.preventDefault();
        if (!yanitDosyaId || !yanitMetin.trim()) return;
        setBusyKey("yanit");
        try {
            await muvekkilMerkez.mesajYanit({
                dosya_id: yanitDosyaId,
                metin: yanitMetin.trim(),
            });
            toast.success("Yanıt gönderildi.");
            setYanitMetin("");
            await Promise.all([loadOzet(), loadMesaj()]);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Gönderilemedi.");
        } finally {
            setBusyKey(null);
        }
    }

    async function openEvrakPreview(id) {
        try {
            await crud.openFile("evraklar", id, "onizle");
        } catch {
            toast.error("Önizleme açılamadı.");
        }
    }

    async function togglePortalAkil(aktif) {
        setBusyKey("portal-akil");
        try {
            await muvekkilMerkez.portalAkilAyar(aktif);
            toast.success(aktif ? "Portal Akıl açıldı." : "Portal Akıl kapatıldı.");
            await loadPortalAkil();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Ayar kaydedilemedi.");
        } finally {
            setBusyKey(null);
        }
    }

    const bekleyenMesajDosyalar = useMemo(() => mesajlar, [mesajlar]);

    return (
        <div className="p-4 md:p-6 max-w-[1500px] mx-auto space-y-5">
            <MerkezHero
                loading={loading}
                onRefresh={refreshAll}
                portalLink={ozet?.portal_link}
            >
                <div className="flex flex-wrap gap-2 mt-4">
                    <Link
                        to="/e/evraklar?portal_kaynak=1"
                        className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border border-white/10 text-slate-400 hover:text-white"
                    >
                        <FileText className="h-3 w-3" /> Portal evrakları
                    </Link>
                    <Link
                        to="/yeni/whatsapp-merkezi"
                        className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border border-white/10 text-slate-400 hover:text-white"
                    >
                        <MessageSquare className="h-3 w-3" /> WhatsApp Merkezi
                    </Link>
                </div>
            </MerkezHero>

            <KpiGrid ozet={ozet} onTabChange={setTab} />

            <div className="flex flex-col lg:flex-row gap-5">
                <aside className="lg:w-56 shrink-0">
                    <div className="lg:hidden flex gap-1 overflow-x-auto pb-2 -mx-1 px-1">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setTab(t.id)}
                                    className={`shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg text-[11px] font-semibold border ${
                                        tab === t.id
                                            ? "bg-[#8b1f2f]/25 border-[#8b1f2f]/40 text-white"
                                            : "border-white/10 text-slate-400"
                                    }`}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="hidden lg:block">
                        <TabSidebar tabs={TABS} activeTab={tab} onTabChange={setTab} ozet={ozet} />
                    </div>
                </aside>

                <main className="flex-1 min-w-0">
            {tab === "ozet" && (
                <OzetPanel
                    ozet={ozet}
                    evraklar={evraklar}
                    aktiviteler={aktiviteler}
                    bekleyenMesajDosyalar={bekleyenMesajDosyalar}
                    yanitDosyaId={yanitDosyaId}
                    setYanitDosyaId={setYanitDosyaId}
                    yanitMetin={yanitMetin}
                    setYanitMetin={setYanitMetin}
                    handleYanit={handleYanit}
                    busyKey={busyKey}
                    onTabChange={setTab}
                />
            )}

            {tab === "onay" && (
                <PanelCard>
                <SectionIntro
                    icon={FileCheck2}
                    title="Portal evrak onay kuyruğu"
                    description="Müvekkil portalından yüklenen belgeleri filtreleyin, önizleyin, onaylayın veya gerekçeli reddedin."
                />
                <section className="space-y-3">
                    <div className="flex flex-wrap gap-2 items-center">
                        {["BEKLIYOR", "ONAYLI", "REDDEDILDI", "TUMU"].map((d) => (
                            <button
                                key={d}
                                type="button"
                                onClick={() => setOnayFiltre(d)}
                                className={`px-3 py-1 rounded-full text-[11px] border ${
                                    onayFiltre === d
                                        ? "border-[#D4AF37]/50 text-[#D4AF37] bg-[#D4AF37]/10"
                                        : "border-white/10 text-slate-400"
                                }`}
                            >
                                {d === "TUMU" ? "Tümü" : d}
                            </button>
                        ))}
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-white/10">
                        <table className="w-full text-sm">
                            <thead className="bg-[#0B0F19]/80 text-[10px] uppercase text-slate-500">
                                <tr>
                                    <th className="text-left p-3">Evrak</th>
                                    <th className="text-left p-3">Dosya / Müvekkil</th>
                                    <th className="text-left p-3">Durum</th>
                                    <th className="text-left p-3">Tarih</th>
                                    <th className="text-right p-3">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {evraklar.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="p-6 text-center text-slate-500">
                                            Kayıt yok.
                                        </td>
                                    </tr>
                                ) : (
                                    evraklar.map((ev) => (
                                        <tr key={ev.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                                            <td className="p-3">
                                                <div className="font-medium text-white">{ev.evrak_turu_label}</div>
                                                <Badge className="mt-1 border-[#8b1f2f]/40 text-[#f0c4cb] bg-[#8b1f2f]/10">
                                                    Portal
                                                </Badge>
                                            </td>
                                            <td className="p-3">
                                                <Link to={ev.dosya_link} className="text-[#D4AF37] hover:underline">
                                                    {ev.dosya_no}
                                                </Link>
                                                <div className="text-xs text-slate-500">{ev.muvekkil_ad}</div>
                                            </td>
                                            <td className="p-3">
                                                <Badge className={ONAY_RENK[ev.onay_durumu] || ONAY_RENK.BEKLIYOR}>
                                                    {ev.onay_durumu_label}
                                                </Badge>
                                                {ev.red_nedeni ? (
                                                    <div className="text-[10px] text-rose-300 mt-1">{ev.red_nedeni}</div>
                                                ) : null}
                                            </td>
                                            <td className="p-3 text-xs text-slate-400">
                                                {ev.yuklenme_tarihi
                                                    ? new Date(ev.yuklenme_tarihi).toLocaleString("tr-TR")
                                                    : "—"}
                                            </td>
                                            <td className="p-3">
                                                <div className="flex flex-wrap justify-end gap-1">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 text-[10px] border-white/10"
                                                        onClick={() => openEvrakPreview(ev.id)}
                                                    >
                                                        Önizle
                                                    </Button>
                                                    {ev.onay_durumu === "BEKLIYOR" ? (
                                                        <>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                className="h-7 text-[10px] bg-emerald-700 hover:bg-emerald-600"
                                                                disabled={busyKey === `onay-${ev.id}`}
                                                                onClick={() => handleOnayla(ev.id)}
                                                            >
                                                                <CheckCircle2 className="h-3 w-3 mr-0.5" />
                                                                Onayla
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="destructive"
                                                                className="h-7 text-[10px]"
                                                                onClick={() => {
                                                                    setRedModal(ev.id);
                                                                    setRedNeden("");
                                                                }}
                                                            >
                                                                <XCircle className="h-3 w-3 mr-0.5" />
                                                                Reddet
                                                            </Button>
                                                        </>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
                </PanelCard>
            )}

            {tab === "mesaj" && (
                <MesajPanel
                    onRefreshOzet={() => {
                        loadOzet().catch(() => {});
                        loadMesaj().catch(() => {});
                    }}
                />
            )}

            {tab === "eksik" && (
                <PanelCard>
                <SectionIntro
                    icon={AlertTriangle}
                    title="Eksik zorunlu evrak"
                    description="Açık dosyalarda MBS 7'li evrak setinde eksik kalan belgeler. Tahkim ve sigorta süreçleri için tamamlanmalıdır."
                />
                <section className="overflow-x-auto rounded-xl border border-white/10">
                    <table className="w-full text-sm">
                        <thead className="bg-[#0B0F19]/80 text-[10px] uppercase text-slate-500">
                            <tr>
                                <th className="text-left p-3">Dosya</th>
                                <th className="text-left p-3">Müvekkil</th>
                                <th className="text-left p-3">Durum</th>
                                <th className="text-left p-3">Eksik belgeler</th>
                                <th className="text-right p-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {eksikDosyalar.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="p-6 text-center text-slate-500">
                                        Eksik evraklı açık dosya yok.
                                    </td>
                                </tr>
                            ) : (
                                eksikDosyalar.map((d) => (
                                    <tr key={d.dosya_id} className="border-t border-white/5">
                                        <td className="p-3 text-white">{d.dosya_no}</td>
                                        <td className="p-3 text-slate-300">{d.muvekkil_ad}</td>
                                        <td className="p-3 text-slate-400">{d.durum}</td>
                                        <td className="p-3">
                                            <div className="flex flex-wrap gap-1">
                                                {d.eksik_evrak.map((ad) => (
                                                    <Badge
                                                        key={ad}
                                                        className="border-amber-500/30 text-amber-200 bg-amber-500/10"
                                                    >
                                                        {ad}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="p-3 text-right">
                                            <Link
                                                to={d.dosya_link}
                                                className="text-xs text-[#D4AF37] hover:underline"
                                            >
                                                Dosya →
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </section>
                </PanelCard>
            )}

            {tab === "rehber" && <RehberPanel onTabChange={setTab} />}

            {tab === "akil" && (
                <section className="space-y-4">
                    <div className="rounded-xl border border-[#D4AF37]/25 bg-[#121A2F]/80 p-5">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div>
                                <h2 className="font-display text-lg text-white flex items-center gap-2">
                                    <Bot className="h-5 w-5 text-[#D4AF37]" />
                                    Müvekkil portal Akıl
                                </h2>
                                <p className="text-sm text-slate-400 mt-1 max-w-2xl">
                                    Müvekkiller <strong className="text-slate-300">/panel/dashboard/</strong>{" "}
                                    ekranında dosya durumu, eksik evrak ve dosya listesi sorabilir.
                                    Akıl otomatik model eğitmez; sorular loglanır ve istatistik üretilir.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <a
                                    href={portalAkil?.portal_link || "https://mbshukuk.com/panel/dashboard/"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md border border-white/10 text-slate-300"
                                >
                                    <ExternalLink className="h-3.5 w-3.5" /> Portalı aç
                                </a>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="border-white/15"
                                    onClick={loadPortalAkil}
                                    disabled={portalAkilLoading}
                                >
                                    {portalAkilLoading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            <Badge
                                className={
                                    portalAkil?.aktif
                                        ? "border-emerald-500/40 text-emerald-200 bg-emerald-500/10"
                                        : "border-rose-500/40 text-rose-200 bg-rose-500/10"
                                }
                            >
                                {portalAkil?.aktif ? "Aktif" : "Kapalı"}
                            </Badge>
                            <Button
                                type="button"
                                size="sm"
                                disabled={busyKey === "portal-akil"}
                                onClick={() => togglePortalAkil(!portalAkil?.aktif)}
                                className="bg-[#D4AF37] text-black hover:bg-[#e8c547]"
                            >
                                {portalAkil?.aktif ? "Kapat" : "Aç"}
                            </Button>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-3">
                        <KpiCard
                            label="Portal soru (30 gün)"
                            value={portalAkil?.ogrenme?.portal_soru_sayisi ?? 0}
                        />
                        <KpiCard
                            label="Başarılı Akıl işlemi"
                            value={portalAkil?.ogrenme?.basarili_islem ?? 0}
                        />
                        <KpiCard
                            label="Toplam log"
                            value={portalAkil?.ogrenme?.toplam_islem ?? 0}
                        />
                    </div>

                    {portalAkil?.ogrenme?.top_intents?.length ? (
                        <div className="rounded-xl border border-white/10 bg-[#121A2F]/50 p-4">
                            <h3 className="text-sm font-semibold text-white mb-2">
                                En çok kullanılan staff komutları
                            </h3>
                            <ul className="text-xs text-slate-400 space-y-1">
                                {portalAkil.ogrenme.top_intents.slice(0, 8).map((row) => (
                                    <li key={row.intent}>
                                        <span className="text-[#F3E5AB]">{row.intent}</span> — {row.adet} kez
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}

                    <div className="rounded-xl border border-white/10 overflow-hidden">
                        <div className="px-4 py-3 border-b border-white/10 text-sm text-slate-400">
                            Son müvekkil soruları
                        </div>
                        {!portalAkil?.son_sorular?.items?.length ? (
                            <p className="py-10 text-center text-sm text-slate-500">
                                Henüz portal sorusu yok. Müvekkil panelinde sağ alttaki{" "}
                                <strong className="text-slate-300">Akıl</strong> butonunu deneyin.
                            </p>
                        ) : (
                            <ul className="divide-y divide-white/5">
                                {portalAkil.son_sorular.items.map((row) => (
                                    <li key={row.id} className="px-4 py-3 text-sm">
                                        <div className="text-slate-200">{row.mesaj || "—"}</div>
                                        <div className="text-[10px] text-slate-500 mt-1">
                                            Müvekkil #{row.muvekkil_id || "—"} ·{" "}
                                            {row.tarih
                                                ? new Date(row.tarih).toLocaleString("tr-TR")
                                                : "—"}{" "}
                                            · {row.sonuc}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>
            )}

            {tab === "tahkim" && (
                <TahkimAkilPanel
                    tahkimDosyalar={tahkimDosyalar}
                    tahkimDosyaId={tahkimDosyaId}
                    setTahkimDosyaId={setTahkimDosyaId}
                    tahkimKarsiPlaka={tahkimKarsiPlaka}
                    setTahkimKarsiPlaka={setTahkimKarsiPlaka}
                    tahkimSigortaBasvuruTarihi={tahkimSigortaBasvuruTarihi}
                    setTahkimSigortaBasvuruTarihi={setTahkimSigortaBasvuruTarihi}
                    tahkimOnizleme={tahkimOnizleme}
                    tahkimChecklist={tahkimChecklist}
                    tahkimBrifing={tahkimBrifing}
                    tahkimRedMetin={tahkimRedMetin}
                    setTahkimRedMetin={setTahkimRedMetin}
                    tahkimRedSonuc={tahkimRedSonuc}
                    tahkimAciklamalar={tahkimAciklamalar}
                    setTahkimAciklamalar={setTahkimAciklamalar}
                    tahkimTalepSonucu={tahkimTalepSonucu}
                    setTahkimTalepSonucu={setTahkimTalepSonucu}
                    tahkimPaketler={tahkimPaketler}
                    tahkimLoading={tahkimLoading}
                    busyKey={busyKey}
                    loadTahkimDosyalar={loadTahkimDosyalar}
                    loadTahkimChecklist={loadTahkimChecklist}
                    handleTahkimBrifing={handleTahkimBrifing}
                    handleTahkimRedAnaliz={handleTahkimRedAnaliz}
                    handleTahkimPaket={handleTahkimPaket}
                    handleTahkimOnizlemeYenile={handleTahkimOnizlemeYenile}
                />
            )}

            {tab === "aktivite" && (
                <PanelCard>
                    <SectionIntro
                        icon={Activity}
                        title="Canlı operasyon akışı"
                        description="Portal evrak yüklemeleri, dosya durum değişiklikleri ve ödeme bildirimleri kronolojik sırayla."
                    />
                    <ActivityTimeline items={aktiviteler} />
                </PanelCard>
            )}

                </main>
            </div>

            {redModal ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                    <div className="w-full max-w-md rounded-xl border border-rose-500/30 bg-[#121A2F] p-5">
                        <h3 className="font-display text-lg text-white mb-2">Evrakı reddet</h3>
                        <p className="text-xs text-slate-400 mb-3">
                            Müvekkil portalında red nedeni görünür.
                        </p>
                        <Textarea
                            value={redNeden}
                            onChange={(e) => setRedNeden(e.target.value)}
                            placeholder="Red gerekçesi…"
                            rows={4}
                            className="bg-[#0B0F19] border-white/10 text-white mb-3"
                        />
                        <div className="flex justify-end gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setRedModal(null)}
                            >
                                İptal
                            </Button>
                            <Button
                                type="button"
                                variant="destructive"
                                disabled={busyKey === `red-${redModal}`}
                                onClick={handleReddet}
                            >
                                Reddet
                            </Button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
